
import React from 'react';
import { SubmissionData } from '../types';
import { CheckCircle, RefreshCcw, Home, FileText, User, Download } from 'lucide-react';

interface SuccessScreenProps {
  data: SubmissionData;
  onReset: () => void;
  onHome: () => void;
  showToast?: (type: 'success' | 'error' | 'info' | 'warning', title: string, message: string) => void;
  currencyCode?: string;
}

export const SuccessScreen: React.FC<SuccessScreenProps> = ({ data, onReset, onHome, showToast, currencyCode }) => {

  const currency = currencyCode || 'QAR';

  const handleDownloadExcel = async () => {
    // Access global ExcelJS loaded via script tag
    const ExcelJS = (window as any).ExcelJS;
    if (!ExcelJS) {
        if (showToast) showToast('error', 'Library Missing', 'Excel generator not fully loaded yet. Please try again.');
        else alert("Excel generator not fully loaded yet.");
        return;
    }

    try {
        if (showToast) showToast('info', 'Generating Report', 'Your Excel file is being created...');
        
        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet('Submission Data');

        sheet.columns = [
            { key: 'id', width: 10 },
            { key: 'name', width: 35 },
            { key: 'sprice', width: 15 },
            { key: 'desc', width: 40 },
            { key: 'platforms', width: 40 },
            { key: 'image', width: 30 },
        ];

        sheet.mergeCells('A1:F2');
        const titleCell = sheet.getCell('A1');
        titleCell.value = 'STERLING DISCOUNT CAMPAIGN - SUBMISSION REPORT';
        titleCell.font = { name: 'Arial', size: 16, bold: true, color: { argb: 'FFFFFFFF' } };
        titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F766E' } };
        titleCell.alignment = { horizontal: 'center', vertical: 'middle' };

        const metaStartRow = 4;
        sheet.getCell(`A${metaStartRow}`).value = 'SUBMISSION DETAILS';
        sheet.getCell(`A${metaStartRow}`).font = { size: 11, bold: true, color: { argb: 'FF0F766E' }, underline: true };
        
        const metaData = [
            ['Submission ID', data.submissionId],
            ['Date Generated', data.timestamp || new Date().toLocaleDateString()],
            ['Manager Name', data.manager.name],
            ['File Number', data.manager.fileNumber],
            ['Selected Brands', data.brands.join(', ')]
        ];

        let currentRow = metaStartRow + 1;
        metaData.forEach(([label, value]) => {
            const labelCell = sheet.getCell(`A${currentRow}`);
            labelCell.value = label;
            labelCell.font = { bold: true, color: { argb: 'FF71717A' } };
            const valCell = sheet.getCell(`B${currentRow}`);
            valCell.value = value;
            valCell.font = { bold: true };
            sheet.mergeCells(`B${currentRow}:D${currentRow}`);
            currentRow++;
        });

        currentRow += 2;
        const headerRowIdx = currentRow;
        const headers = ['Item #', 'Item Name', 'Original Price', 'Description', 'Target Platforms', 'Image Filename'];
        
        headers.forEach((text, idx) => {
            const colLetter = String.fromCharCode(65 + idx);
            const cell = sheet.getCell(`${colLetter}${headerRowIdx}`);
            cell.value = text;
            cell.font = { bold: true, color: { argb: 'FF042F2E' } };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F0FDFA' } };
            cell.border = {
                top: { style: 'medium', color: { argb: 'FF0F766E' } },
                bottom: { style: 'medium', color: { argb: 'FF0F766E' } },
            };
            cell.alignment = { vertical: 'middle', horizontal: 'center' };
        });

        currentRow++;
        data.items.forEach((item, index) => {
            const rowNum = currentRow;
            const rowValues = [
                index + 1,
                item.name,
                parseFloat(item.sellingPrice) || 0,
                item.description || '-',
                item.selectedAggregators.join(', '),
                item.imageName || 'No Image Attached'
            ];

            rowValues.forEach((val, idx) => {
                const colLetter = String.fromCharCode(65 + idx);
                const cell = sheet.getCell(`${colLetter}${rowNum}`);
                cell.value = val;
                if (idx === 2) { 
                  cell.numFmt = `"${currency}" #,##0.00`; 
                    cell.alignment = { horizontal: 'center' };
                }
            });
            currentRow++;
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Sterling_Submission_${data.submissionId}.xlsx`;
        link.click();
        window.URL.revokeObjectURL(url);
        
        if (showToast) showToast('success', 'Download Complete', 'The submission report has been downloaded.');

    } catch (error) {
        console.error(error);
        if (showToast) showToast('error', 'Export Failed', 'An error occurred while generating the Excel file.');
    }
  };

  return (
    <div className="max-w-2xl mx-auto text-center py-12 px-4">
      <div className="flex flex-col sm:flex-row justify-center gap-4 mb-10">
        <button onClick={onReset} className="inline-flex justify-center items-center px-6 py-3 border border-zinc-300 shadow-sm text-sm font-bold rounded-xl text-zinc-700 bg-white hover:bg-zinc-50 hover:border-zinc-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 transition-all">
          <RefreshCcw className="mr-2 h-4 w-4" /> Submit Another Request
        </button>
        <button onClick={onHome} className="inline-flex justify-center items-center px-6 py-3 border border-transparent shadow-sm text-sm font-bold rounded-xl text-white bg-zinc-800 hover:bg-zinc-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-zinc-500 transition-all">
            <Home className="mr-2 h-4 w-4" /> Dashboard
        </button>
      </div>

      <div className="mb-8 flex justify-center">
        <div className="rounded-full bg-emerald-100 p-6 ring-8 ring-emerald-50 shadow-xl"><CheckCircle className="w-16 h-16 text-emerald-600" strokeWidth={3} /></div>
      </div>
      
      <h2 className="text-4xl font-extrabold text-zinc-900 mb-4 tracking-tight">Submission Received!</h2>
      <p className="text-lg text-zinc-600 mb-10 max-w-md mx-auto">Your items have been successfully sent to the Admin team.</p>

      <div className="bg-white rounded-3xl shadow-xl shadow-zinc-200/60 border border-zinc-100 overflow-hidden max-w-md mx-auto mb-10 text-left relative">
        <div className="h-2 bg-gradient-to-r from-teal-500 to-emerald-500 absolute top-0 left-0 right-0"></div>
        <div className="p-8">
            <div className="flex justify-between items-start mb-6 border-b border-zinc-100 pb-6">
                <div><span className="block text-zinc-400 text-xs font-bold uppercase tracking-wider mb-1">Submission ID</span><span className="font-mono text-xl font-bold text-zinc-800">{data.submissionId}</span></div>
                <div className="text-right"><span className="block text-zinc-400 text-xs font-bold uppercase tracking-wider mb-1">Date</span><span className="font-medium text-zinc-700">{data.timestamp ? new Date(data.timestamp).toLocaleString() : new Date().toLocaleString()}</span></div>
            </div>
            <div className="space-y-4">
                <div className="flex justify-between group"><span className="text-zinc-500 flex items-center gap-2 text-sm"><User className="w-4 h-4"/> Manager</span><span className="font-bold text-zinc-900">{data.manager.name}</span></div>
                <div className="flex justify-between group"><span className="text-zinc-500 flex items-center gap-2 text-sm"><Home className="w-4 h-4"/> Brand(s)</span><span className="font-bold text-zinc-900 text-right max-w-[200px]">{data.brands.join(', ')}</span></div>
            </div>
            <div className="mt-8 bg-zinc-50 rounded-xl p-4 flex justify-between items-center border border-zinc-100">
                <span className="text-zinc-600 font-medium">Items Submitted</span><span className="bg-white text-zinc-900 px-3 py-1 rounded-lg font-bold shadow-sm border border-zinc-200">{data.items.length}</span>
            </div>
        </div>
        <div className="bg-zinc-50 px-8 py-4 border-t border-zinc-100">
             <button onClick={handleDownloadExcel} className="w-full flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-bold py-3 rounded-xl transition-all shadow-lg shadow-teal-200 active:scale-95">
                <Download className="w-4 h-4" /> Download Excel Report
             </button>
        </div>
      </div>
    </div>
  );
};
